import { AxiosResponse } from 'axios'
import { IFilter, Where } from 'types/common'
import { AggregationPipeline } from 'types/common/aggregation'
import { api } from 'API'
import { IDuplicateGroupDTO, IUpdateGroupMember } from 'components/Pages/UserPage/types'
import { AggregationPaginated } from 'interfaces/common'
import { IAggregateGroupMemberResult, IGroup, IGroupMember, IGroupMemberPermission } from 'interfaces/group'

export async function getGroupsByAggregate<T = IGroup>(pipeline: AggregationPipeline): Promise<T[]> {
  try {
    const response: AxiosResponse = await api.post(`/groups/aggregate`, {
      pipeline
    })
    return response?.data
  } catch (error: any) {
    return []
  }
}

export async function aggregateCountGroups(pipeline: AggregationPipeline): Promise<number> {
  try {
    const response: AxiosResponse = await api.post(`/groups/aggregate/count`, {
      pipeline
    })
    return response?.data[0]?.totalResults ?? 0
  } catch (error: any) {
    return 0
  }
}

export async function aggregateGroupSearch(pipeline: AggregationPipeline): Promise<AggregationPaginated<IGroup>> {
  try {
    const response: AxiosResponse = await api.post(`/groups/aggregate/search`, {
      pipeline
    })
    return response?.data
  } catch (error: any) {
    return { paginatedResults: [], totalResults: 0 }
  }
}

export async function duplicateGroup(sourceGroupId: number, duplicateGroupDto: IDuplicateGroupDTO): Promise<IGroup> {
  const response: AxiosResponse = await api.post(`/groups/${sourceGroupId}/duplicate`, duplicateGroupDto)
  return response?.data
}

export async function getGroups(filter: IFilter<IGroup> = {}): Promise<IGroup[]> {
  const response: AxiosResponse = await api.get('/groups', { params: { filter } })
  return response.data
}

export async function getGroupById(id: number): Promise<IGroup> {
  const response: AxiosResponse = await api.get(`/groups/${id}`)
  return response.data
}

export async function countGroups(where: Where<IGroup> = {}): Promise<number> {
  const response: AxiosResponse = await api.get('/groups/count', { params: { where } })
  return +(response.data.count ?? 0)
}

export async function deleteGroupById(id: number): Promise<void> {
  await api.delete(`/groups/${id}`)
}

export async function createGroup(group: IGroup): Promise<void> {
  await api.post(`/groups`, group)
}

export async function updateGroupById(id: number, updatedGroup: IGroup): Promise<void> {
  await api.patch(`/groups/${id}`, updatedGroup)
}

export async function addGroupMembers(groupMembers: IGroupMember[]): Promise<IGroupMember[]> {
  const response: AxiosResponse = await api.post('/group-members/batch-update', { toCreateGroupMembers: groupMembers })
  return response.data
}

export async function updateGroupMemberById(id: number, updatedGroupMember: Partial<IGroupMember>): Promise<void> {
  await api.patch(`/group-members/${id}`, updatedGroupMember)
}

export async function removeGroupMemberById(id: number): Promise<void> {
  if (!id) {
    return
  }
  await api.delete(`/group-members/${id}`)
}

export async function updateGroupMembers(data: IUpdateGroupMember): Promise<void> {
  const response: AxiosResponse = await api.post('/group-members/batch-update', data)
  return response.data
}

export async function getGroupMemberPermissions(
  filter: IFilter<IGroupMemberPermission> = {}
): Promise<IGroupMemberPermission[]> {
  const response: AxiosResponse = await api.get('/group-member-permissions', { params: { filter } })
  return response.data
}

export async function getGroupMembers(id: number, filter: IFilter<IGroup> = {}): Promise<IAggregateGroupMemberResult> {
  const response: AxiosResponse = await api.get(`/groups/${id}/aggregate-group-member`, { params: { filter } })
  return response.data
}

export async function getNumberOfGroupMember(id: number): Promise<number> {
  const response: AxiosResponse = await api.get(`/groups/${id}/count-member`)
  return response.data.count
}

export async function getGroupMembersFilter(filter: IFilter<IGroupMember> = {}): Promise<IGroupMember[]> {
  const response: AxiosResponse = await api.get(`/group-members?filter=${JSON.stringify(filter)}`)
  return response.data
}
